// AlignUI Toast v0.0.0
//
// Копия один в один, с тремя обычными правками переноса: снята директива клиентского
// компонента, алиасы у нас те же, и числовое скругление шестнадцатой ступени заменено на
// `rounded-2xl`.
//
// Последнее — не вкусовщина. В нашей теме числовых скруглений ровно два, десятое и двадцатое;
// шестнадцатого у нас не существует, Tailwind молча выбрасывает такой класс, и всплывающее
// подтверждение вышло бы с ПРЯМЫМИ углами. `rounded-2xl` — это те же шестнадцать точек, и ровно
// им набран popover, то есть роль «всплывающий список» уже занята этим классом. Поймал это
// сторож оформления на первом же прогоне.

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import {
  RiCheckboxCircleFill,
  RiCloseLine,
  RiErrorWarningFill,
  RiInformationFill,
  RiSpam2Fill,
} from '@remixicon/react';

import type { PolymorphicComponentProps } from '@/utils/polymorphic';
import { recursiveCloneChildren } from '@/utils/recursive-clone-children';
import { cnExt } from '@/utils/cn';
import { tv, type VariantProps } from '@/utils/tv';

const TOAST_PROVIDER_NAME = 'ToastProvider';
const TOAST_VIEWPORT_NAME = 'ToastViewport';
const TOAST_ROOT_NAME = 'ToastRoot';
const TOAST_ICON_NAME = 'ToastIcon';
const TOAST_CONTENT_NAME = 'ToastContent';
const TOAST_TITLE_NAME = 'ToastTitle';
const TOAST_DESCRIPTION_NAME = 'ToastDescription';
const TOAST_ACTION_NAME = 'ToastAction';
const TOAST_CLOSE_NAME = 'ToastClose';

/**
 * Toast — AlignUI-реализация без @radix-ui/react-toast (не установлен в проекте).
 * Построено на React Context + portal-free viewport (фиксированный контейнер).
 *
 * Декларативный режим (static):
 *   <Toast.Provider>
 *     <Toast.Root variant="success" duration={5000}>
 *       <Toast.Icon />
 *       <Toast.Content>
 *         <Toast.Title>Saved</Toast.Title>
 *         <Toast.Description>Changes applied</Toast.Description>
 *       </Toast.Content>
 *       <Toast.Close />
 *     </Toast.Root>
 *     <Toast.Viewport />
 *   </Toast.Provider>
 *
 * Императивный режим (через context):
 *   const { addToast } = Toast.useToast();
 *   addToast({ title: 'Saved', description: '...', variant: 'success' });
 */

type ToastVariant = 'info' | 'success' | 'warning' | 'error' | 'default';

export const toastVariants = tv({
  slots: {
    root: [
      // base
      'pointer-events-auto group/toast relative flex w-full items-start gap-3 overflow-hidden rounded-2xl bg-bg-white-0 p-3.5 shadow-regular-md ring-1 ring-inset ring-stroke-soft-200',
      // animation
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-80 data-[state=open]:fade-in-0',
      'data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full',
      'data-[swipe=end]:animate-out data-[swipe=end]:slide-out-to-right-full',
      'data-[swipe=cancel]:translate-x-0 data-[swipe=move]:translate-x-[var(--swipe-move-x,0)]',
      'data-[swipe=move]:transition-none data-[swipe=cancel]:transition-transform',
    ],
    icon: 'size-5 shrink-0',
    content: 'flex min-w-0 flex-1 flex-col gap-1',
    title: 'text-label-sm text-text-strong-950',
    description: 'text-paragraph-sm text-text-sub-600',
    action: [
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md bg-primary-base px-3 text-label-sm text-static-white outline-none',
      'transition-colors duration-200 ease-out',
      'hover:bg-primary-darker',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-base/40',
    ],
    close: [
      // base
      'inline-flex size-6 shrink-0 items-center justify-center rounded-md text-text-soft-400 outline-none',
      'transition-colors duration-200 ease-out',
      'hover:bg-bg-weak-50 hover:text-text-strong-950',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-stroke-strong-950/10',
    ],
  },
  variants: {
    variant: {
      default: {
        icon: 'text-text-sub-600',
      },
      info: {
        icon: 'text-information-base',
      },
      success: {
        icon: 'text-success-base',
      },
      warning: {
        icon: 'text-warning-base',
      },
      error: {
        icon: 'text-error-base',
      },
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type ToastSharedProps = VariantProps<typeof toastVariants>;

// ============ Context ============

export interface ToastItem {
  id: string;
  variant?: ToastVariant;
  title?: React.ReactNode;
  description?: React.ReactNode;
  duration?: number;
  action?: React.ReactNode;
}

interface ToastContextValue {
  toasts: ToastItem[];
  addToast: (toast: Omit<ToastItem, 'id'> & { id?: string }) => string;
  dismissToast: (id: string) => void;
  removeToast: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

function useToast() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error('Toast.useToast must be used within <Toast.Provider>');
  }
  return ctx;
}

const DEFAULT_DURATION = 5000;

interface ToastProviderProps {
  children?: React.ReactNode;
  /** Глобальная длительность по умолчанию (ms). Можно переопределить на уровне toast.duration. */
  duration?: number;
  /** Максимальное количество одновременно видимых toast. */
  limit?: number;
  swipeDirection?: 'right' | 'left' | 'up' | 'down';
}

const ToastProvider = ({
  children,
  duration: providerDuration = DEFAULT_DURATION,
  limit = 5,
}: ToastProviderProps) => {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const timersRef = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const dismissToast = React.useCallback(
    (id: string) => {
      removeToast(id);
    },
    [removeToast],
  );

  const addToast = React.useCallback<ToastContextValue['addToast']>(
    (toast) => {
      const id = toast.id ?? `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const effectiveDuration = toast.duration ?? providerDuration;

      setToasts((prev) => {
        const next: ToastItem[] = [...prev, { ...toast, id }];
        // соблюдаем limit: убираем самые старые
        if (next.length > limit) {
          const removed = next.slice(0, next.length - limit);
          removed.forEach((r) => {
            const timer = timersRef.current.get(r.id);
            if (timer) {
              clearTimeout(timer);
              timersRef.current.delete(r.id);
            }
          });
          return next.slice(next.length - limit);
        }
        return next;
      });

      if (effectiveDuration && effectiveDuration > 0) {
        const timer = setTimeout(() => {
          dismissToast(id);
        }, effectiveDuration);
        timersRef.current.set(id, timer);
      }
      return id;
    },
    [providerDuration, limit, dismissToast],
  );

  React.useEffect(() => {
    // cleanup on unmount — copy ref to stable local
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const value = React.useMemo(
    () => ({ toasts, addToast, dismissToast, removeToast }),
    [toasts, addToast, dismissToast, removeToast],
  );

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
};
ToastProvider.displayName = TOAST_PROVIDER_NAME;

// ============ Viewport ============

type ToastViewportProps = React.HTMLAttributes<HTMLOListElement> & {
  /** Кастомный рендерер для imperative toast. По умолчанию — дефолтный layout. */
  renderToast?: (toast: ToastItem, dismiss: () => void) => React.ReactNode;
};

const ToastViewport = React.forwardRef<HTMLOListElement, ToastViewportProps>(
  ({ className, children, renderToast, ...rest }, forwardedRef) => {
    const ctx = React.useContext(ToastContext);

    return (
      <ol
        ref={forwardedRef}
        role='region'
        // Подпись по-русски: её читает вслух озвучивание экрана, и «Notifications» человек
        // услышит машинным словом на чужом языке. В переносе из дизайн-системы она осталась
        // английской, поправлено 04.09.2026.
        aria-label='Подтверждения'
        aria-live='polite'
        aria-atomic='false'
        className={cnExt(
          'pointer-events-none fixed right-4 top-4 z-[100] flex max-h-screen w-full max-w-95 flex-col gap-2 outline-none sm:right-6 sm:top-6',
          className,
        )}
        {...rest}
      >
        {children}
        {ctx?.toasts.map((toast) => {
          const dismiss = () => ctx.dismissToast(toast.id);
          if (renderToast) {
            return (
              <React.Fragment key={toast.id}>
                {renderToast(toast, dismiss)}
              </React.Fragment>
            );
          }
          return (
            <ToastRoot key={toast.id} variant={toast.variant} data-подтверждение=''>
              <ToastIcon />
              <ToastContent>
                {toast.title && <ToastTitle>{toast.title}</ToastTitle>}
                {toast.description && (
                  <ToastDescription>{toast.description}</ToastDescription>
                )}
              </ToastContent>
              {toast.action}
              <ToastClose onClick={dismiss} />
            </ToastRoot>
          );
        })}
      </ol>
    );
  },
);
ToastViewport.displayName = TOAST_VIEWPORT_NAME;

// ============ Root ============

type ToastRootProps = React.HTMLAttributes<HTMLLIElement> &
  ToastSharedProps & {
    /** ms. 0 или undefined → без автозакрытия. */
    duration?: number;
    /** Callback при авто-закрытии / программном закрытии. */
    onOpenChange?: (open: boolean) => void;
    defaultOpen?: boolean;
    /** Уникальный id для управления через useToast(). */
    toastId?: string;
  };

const ToastRoot = React.forwardRef<HTMLLIElement, ToastRootProps>(
  (
    {
      children,
      className,
      variant,
      duration,
      defaultOpen = true,
      onOpenChange,
      toastId,
      ...rest
    },
    forwardedRef,
  ) => {
    const uniqueId = React.useId();
    const [open, setOpen] = React.useState(defaultOpen);
    const { root } = toastVariants({ variant });

    React.useEffect(() => {
      if (!open || !duration || duration <= 0) return;
      const t = setTimeout(() => {
        setOpen(false);
        onOpenChange?.(false);
      }, duration);
      return () => clearTimeout(t);
    }, [open, duration, onOpenChange]);

    const sharedProps: ToastSharedProps = { variant };

    const extendedChildren = recursiveCloneChildren(
      children as React.ReactElement[],
      sharedProps,
      [
        TOAST_ICON_NAME,
        TOAST_CONTENT_NAME,
        TOAST_TITLE_NAME,
        TOAST_DESCRIPTION_NAME,
        TOAST_ACTION_NAME,
        TOAST_CLOSE_NAME,
      ],
      uniqueId,
    );

    if (!open) return null;

    return (
      <li
        ref={forwardedRef}
        data-state={open ? 'open' : 'closed'}
        data-variant={variant}
        data-toast-id={toastId}
        className={root({ class: className })}
        {...rest}
      >
        {extendedChildren}
      </li>
    );
  },
);
ToastRoot.displayName = TOAST_ROOT_NAME;

// ============ Icon ============

function defaultIconFor(variant: ToastVariant | null | undefined): React.ElementType {
  switch (variant) {
    case 'success':
      return RiCheckboxCircleFill;
    case 'warning':
      return RiErrorWarningFill;
    case 'error':
      return RiSpam2Fill;
    case 'info':
    case 'default':
    default:
      return RiInformationFill;
  }
}

function ToastIcon<T extends React.ElementType>({
  className,
  as,
  variant,
  ...rest
}: PolymorphicComponentProps<T, ToastSharedProps>) {
  const Component = as || defaultIconFor(variant);
  const { icon } = toastVariants({ variant });

  return <Component className={icon({ class: className })} {...rest} />;
}
ToastIcon.displayName = TOAST_ICON_NAME;

// ============ Content / Title / Description ============

const ToastContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, forwardedRef) => {
  const { content } = toastVariants();

  return (
    <div
      ref={forwardedRef}
      className={content({ class: className })}
      {...rest}
    />
  );
});
ToastContent.displayName = TOAST_CONTENT_NAME;

const ToastTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, forwardedRef) => {
  const { title } = toastVariants();

  return (
    <div ref={forwardedRef} className={title({ class: className })} {...rest} />
  );
});
ToastTitle.displayName = TOAST_TITLE_NAME;

const ToastDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, forwardedRef) => {
  const { description } = toastVariants();

  return (
    <div
      ref={forwardedRef}
      className={description({ class: className })}
      {...rest}
    />
  );
});
ToastDescription.displayName = TOAST_DESCRIPTION_NAME;

// ============ Action ============

type ToastActionProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
};

const ToastAction = React.forwardRef<HTMLButtonElement, ToastActionProps>(
  ({ className, type = 'button', asChild, ...rest }, forwardedRef) => {
    const { action } = toastVariants();
    const Component = asChild ? Slot : 'button';

    return (
      <Component
        ref={forwardedRef as React.Ref<HTMLButtonElement>}
        type={asChild ? undefined : type}
        className={action({ class: className })}
        {...rest}
      />
    );
  },
);
ToastAction.displayName = TOAST_ACTION_NAME;

// ============ Close ============

const ToastClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, type = 'button', ...rest }, forwardedRef) => {
  const { close } = toastVariants();

  return (
    <button
      ref={forwardedRef}
      type={type}
      aria-label='Close toast'
      className={close({ class: className })}
      {...rest}
    >
      {children ?? <RiCloseLine className='size-4' aria-hidden='true' />}
    </button>
  );
});
ToastClose.displayName = TOAST_CLOSE_NAME;

export {
  ToastProvider as Provider,
  ToastViewport as Viewport,
  ToastRoot as Root,
  ToastIcon as Icon,
  ToastContent as Content,
  ToastTitle as Title,
  ToastDescription as Description,
  ToastAction as Action,
  ToastClose as Close,
  useToast,
};
